import { publicAssetUrl } from "@/lib/public-image-url";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ApexScreenCar } from "./ApexScreenCar";
import "./apex-seen-in.css";

const POSTERS = publicAssetUrl("movie-posters");

/** Existing public site poster assets, verified read-only; no poster API/cache writes. */
export const APEX_SCREEN_CLASSICS = [
  { title: "Bullitt", year: 1968, car: "Ford Mustang GT", poster: "bullitt-1968-9ceae3b4.jpg" },
  { title: "Goldfinger", year: 1964, car: "Aston Martin DB5", poster: "goldfinger-1964-b73df740.jpg" },
  { title: "The Italian Job", year: 1969, car: "Mini Cooper S", poster: "the-italian-job-1969-e0c560f2.jpg" },
  { title: "Back to the Future", year: 1985, car: "DeLorean DMC-12", poster: "back-to-the-future-1985-a2006b1e.jpg" },
  { title: "The Love Bug", year: 1968, car: "Volkswagen Beetle", poster: "the-love-bug-1968-4a66ba38.jpg" },
  { title: "Smokey and the Bandit", year: 1977, car: "Pontiac Firebird Trans Am", poster: "smokey-and-the-bandit-1977-3d2bf53f.jpg" },
] as const;

type Mode = "grid" | "stage" | "mobile-stage" | "strip";
const N = APEX_SCREEN_CLASSICS.length;

function ScreenClassic({ film, index, active, layered }: { film: typeof APEX_SCREEN_CLASSICS[number]; index: number; active: boolean; layered: boolean }) {
  const [failed, setFailed] = useState(false);
  // Layered stages stack all six films; hidden layers are inert (non-interactive, non-focusable).
  const hidden = layered && !active;
  const ref = useRef<HTMLElement>(null);
  useEffect(() => { ref.current?.toggleAttribute("inert", hidden); }, [hidden]);
  return <figure className="min-w-0" data-year={film.year} data-active={active ? "true" : "false"} data-testid={`figure-seen-in-${index}`}
    aria-hidden={hidden || undefined} ref={ref}>
    <div className="sx-poster">
      {failed ? <div className="sx-fail">Poster unavailable</div> :
        <img src={`${POSTERS}/${film.poster}`} width={342} height={513} loading="lazy" decoding="async"
          alt={`${film.title} (${film.year}) film poster`} onError={() => setFailed(true)} />}
    </div>
    <figcaption className="sx-cap">
      <p className="sx-kick" aria-hidden>Scene {String(index + 1).padStart(2, "0")} / {String(N).padStart(2, "0")}</p>
      <h3 className="sx-title">{film.title}</h3>
      <p className="sx-year">{film.year}</p>
      <p className="sx-car">{film.car}</p>
      <ApexScreenCar title={film.title} index={index} />
    </figcaption>
  </figure>;
}

export function ApexSeenIn() {
  const [mode, setMode] = useState<Mode>("grid");
  const [active, setActive] = useState(0);
  const matrixRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const framesRef = useRef<HTMLDivElement>(null);

  // Choose enhancement after hydration; SSR/no-JS/reduced-motion keep the static grid.
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const wide = window.matchMedia("(min-width: 1024px) and (min-height: 800px)");
    const phone = window.matchMedia("(max-width: 767px)");
    // Phone stage needs room for poster + title + car beneath header/browser chrome; else static column.
    const tall = window.matchMedia("(min-height: 520px)");
    const all = [reduce, wide, phone, tall];
    const pick = () => setMode(reduce.matches ? "grid" : wide.matches ? "stage"
      : phone.matches ? (tall.matches ? "mobile-stage" : "grid") : "strip");
    pick();
    all.forEach(q => q.addEventListener("change", pick));
    return () => all.forEach(q => q.removeEventListener("change", pick));
  }, []);

  // Desktop + phone: native page scroll drives the sticky sequence (no hijacking, no preventDefault).
  useEffect(() => {
    if (mode !== "stage" && mode !== "mobile-stage") return;
    const el = matrixRef.current, stage = stageRef.current;
    if (!el || !stage) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = el.getBoundingClientRect();
      const top = parseFloat(getComputedStyle(stage).top) || 0;
      const span = Math.max(1, rect.height - stage.offsetHeight);
      const p = Math.min(1, Math.max(0, (top - rect.top) / span));
      const idx = Math.min(N - 1, Math.floor(p * N));
      el.style.setProperty("--sx-p", p.toFixed(4));
      el.style.setProperty("--sx-l", (p * N - idx).toFixed(4));
      setActive(idx);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [mode]);

  // Phone: track the snapped card in the swipeable strip.
  useEffect(() => {
    if (mode !== "strip") return;
    const strip = framesRef.current, el = matrixRef.current;
    if (!strip || !el) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const max = Math.max(1, strip.scrollWidth - strip.clientWidth);
      const p = Math.min(1, strip.scrollLeft / max);
      el.style.setProperty("--sx-p", p.toFixed(4));
      setActive(Math.round(p * (N - 1)));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    strip.addEventListener("scroll", onScroll, { passive: true });
    return () => { strip.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [mode]);

  const jump = useCallback((i: number) => {
    if (mode === "stage" || mode === "mobile-stage") {
      const el = matrixRef.current, stage = stageRef.current;
      if (!el || !stage) return;
      const rect = el.getBoundingClientRect();
      const top = parseFloat(getComputedStyle(stage).top) || 0;
      const span = Math.max(1, rect.height - stage.offsetHeight);
      window.scrollTo({ top: window.scrollY + rect.top - top + ((i + 0.5) / N) * span, behavior: "smooth" });
    } else if (mode === "strip") {
      const fig = framesRef.current?.children[i] as HTMLElement | undefined;
      const strip = framesRef.current;
      if (fig && strip && typeof strip.scrollTo === "function") strip.scrollTo({ left: fig.offsetLeft - (strip.firstElementChild as HTMLElement).offsetLeft, behavior: "smooth" });
    }
    setActive(i);
  }, [mode]);

  const enhanced = mode !== "grid";
  const layered = mode === "stage" || mode === "mobile-stage";

  return <section aria-labelledby="apex-seen-in-heading" className="sx-root">
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
      <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
        <div>
          <p className="ax-kicker" style={{ color: "var(--color-brand-light)" }}>Cultural Relevance · up to 10 points</p>
          <h2 id="apex-seen-in-heading" className="ax-display mt-4 text-3xl sm:text-4xl" style={{ color: "var(--color-brand-editorial-main)" }}>Seen in</h2>
        </div>
        <div className="space-y-4 leading-relaxed" style={{ color: "color-mix(in srgb, var(--color-brand-dark-ink) 80%, transparent)" }}>
          <p>Sometimes the first time you fall for a classic is at the cinema. A chase through San Francisco, a secret agent’s DB5 or a time-travelling DeLorean can make a model part of our shared memory.</p>
          <p>That is why we show screen appearances: they give context to a car’s cultural story, one part of APEX’s Cultural Relevance factor. These familiar pairings illustrate the connection; posters are not extra points, a guest review or an endorsement.</p>
          <p className="text-sm">These are model-level film connections, not appearances by the Bel Air example above. A matching model on a listing does not mean that particular vehicle was used on screen, is an original film car or has verified film provenance.</p>
        </div>
      </div>

      <div ref={matrixRef} className="sx-matrix" data-mode={mode} data-testid="apex-seen-in-matrix">
        <div ref={stageRef} className="sx-stage">
          <div ref={framesRef} className="sx-frames">
            {APEX_SCREEN_CLASSICS.map((film, i) => <ScreenClassic key={film.title} film={film} index={i} active={!enhanced || i === active} layered={layered} />)}
          </div>
          {enhanced && <div className="sx-dock">
            <div className="sx-progress" aria-hidden><span /></div>
            {mode === "mobile-stage" && <p className="sx-now" aria-live="polite" data-testid="text-seen-in-now">
              <span className="sx-count-n">{String(active + 1).padStart(2, "0")}</span> / {String(N).padStart(2, "0")}
              <span className="sx-now-t"> · {APEX_SCREEN_CLASSICS[active].title}</span>
            </p>}
            {mode === "strip" && <div className="sx-step">
              <button type="button" className="sx-arrow" aria-label="Previous film" disabled={active === 0}
                onClick={() => jump(Math.max(0, active - 1))} data-testid="button-seen-in-prev"><ChevronLeft aria-hidden className="h-5 w-5" /></button>
              <p className="sx-count" aria-live="polite" data-testid="text-seen-in-count">
                <span className="sx-count-n">{String(active + 1).padStart(2, "0")}</span> / {String(N).padStart(2, "0")}
                <span className="sx-count-t">{APEX_SCREEN_CLASSICS[active].title}</span>
              </p>
              <button type="button" className="sx-arrow" aria-label="Next film" disabled={active === N - 1}
                onClick={() => jump(Math.min(N - 1, active + 1))} data-testid="button-seen-in-next"><ChevronRight aria-hidden className="h-5 w-5" /></button>
            </div>}
            <div className="sx-controls" role="group" aria-label="Choose a film">
              {APEX_SCREEN_CLASSICS.map((film, i) => (
                <button key={film.title} type="button" className="sx-btn" aria-current={i === active ? "true" : "false"}
                  aria-label={`Show ${film.title} (${film.year}), ${film.car}`} onClick={() => jump(i)} data-testid={`button-seen-in-${i}`}>
                  <img src={`${POSTERS}/${film.poster}`} alt="" width={56} height={84} loading="lazy" decoding="async"
                    onError={event => { event.currentTarget.style.visibility = "hidden"; }} />
                </button>
              ))}
            </div>
          </div>}
        </div>
      </div>

      <p className="mt-10 max-w-3xl text-xs leading-relaxed" style={{ color: "color-mix(in srgb, var(--color-brand-dark-ink) 60%, transparent)" }}>
        Related cars are public listing snapshots recorded 4 November 2026; they share a model family with the film car but are not screen-used vehicles, and availability, specification and prices may differ or change. Film posters © their respective rights holders. Poster artwork supplied through the site’s existing TMDB-backed poster library.
        {" "}<a href="https://www.themoviedb.org/" target="_blank" rel="noopener noreferrer" className="ax-link underline underline-offset-4">TMDB</a> does not endorse or certify this page.
      </p>
    </div>
  </section>;
}
